import { cn } from '../lib/cn';

/**
 * Employee avatar.
 *
 * Falls back to initials on a deterministic tint so the same person is the same
 * colour on every screen — a small thing that makes a directory scannable.
 */

const TINTS = [
  'bg-brand-soft text-brand-hover',
  'bg-success-soft text-success',
  'bg-warning-soft text-warning',
  'bg-accent-soft text-accent',
  'bg-info-soft text-info',
  'bg-neutral-soft text-ink-secondary',
];

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`.toUpperCase();
}

function tintFor(name: string): string {
  let hash = 5381;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 33) ^ name.charCodeAt(i);
  return TINTS[Math.abs(hash) % TINTS.length]!;
}

const SIZES = {
  xs: 'h-6 w-6 text-micro',
  sm: 'h-8 w-8 text-caption',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
} as const;

export function Avatar({
  name,
  imageUrl,
  size = 'md',
  className,
}: {
  name: string;
  imageUrl?: string | null;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold',
        SIZES[size],
        imageUrl ? 'bg-neutral-soft' : tintFor(name),
        className,
      )}
      aria-hidden
    >
      {imageUrl ? (
        <img src={imageUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        initials(name)
      )}
    </span>
  );
}

/** Avatar + name + secondary line. The single way a person is identified in a list. */
export function PersonCell({
  name,
  secondary,
  imageUrl,
  size = 'sm',
}: {
  name: string;
  secondary?: string | null;
  imageUrl?: string | null;
  size?: keyof typeof SIZES;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <Avatar name={name} imageUrl={imageUrl} size={size} />
      <div className="min-w-0">
        <div className="truncate text-base font-medium text-ink">{name}</div>
        {secondary ? <div className="truncate text-caption text-ink-muted">{secondary}</div> : null}
      </div>
    </div>
  );
}
